import { Input, InputProps } from "antd";
import { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { observer } from "mobx-react-lite";

import { ListStoreConstructor } from "stores/ListStoreConstructor";

import defaultInputStyles from "../DefaultInput/input.module.css";
import styles from "./search-input.module.css";
import { ReactComponent as SearchIcon } from "images/search.svg";

export interface SearchInputProps extends Omit<InputProps, "prefix"> {
  onSearch?: (value: string) => void;
  store: ListStoreConstructor<any>;
}

export const SearchInput = observer(({ className, style, onSearch, store, ...props }: SearchInputProps) => {
  const [searchValue, setSearchValue] = useState(store.queryParams["searchTerm"] || "");

  const performSearch = useCallback(
    (value: string) => {
      store.onChangeDataFlow({
        paginationData: {
          currentPage: 1,
          pageSize: store.paginationData.pageSize,
        },
        filters: {
          ...store.filters,
          searchTerm: value || undefined,
        },
        sorters: store.sorters,
      });
    },
    [store]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      if (value.length >= 3) {
        performSearch(value);
      } else if (value.length === 0) {
        performSearch("");
      }
    }, 500),
    [store, performSearch]
  );

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        debouncedSearch.cancel();
        performSearch(searchValue);
      }
    },
    [debouncedSearch, performSearch, searchValue]
  );

  useEffect(() => {
    if (!store.filters?.searchTerm) {
      setSearchValue("");
    }
  }, [store.filters]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <label className={`${defaultInputStyles.inputBlock} ${className}`}>
      <Input
        {...props}
        classNames={{
          prefix: `${styles.inputPrefix}`,
        }}
        className={`${defaultInputStyles.input} ${styles.input}`}
        variant="outlined"
        placeholder=""
        prefix={<SearchIcon />}
        value={searchValue}
        onChange={handleSearch}
        onKeyDown={handleKeyDown}
      >
        {props.children}
      </Input>
      {props.placeholder && (
        <span
          className={`${defaultInputStyles.label} ${styles.label} ${
            searchValue ? `${defaultInputStyles.labelTop} ${styles.labelTop}` : ""
          }`}
        >
          {props.placeholder}
        </span>
      )}
    </label>
  );
});
